import { FC } from "react";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import { Switch } from "@src/lib/ui/Switch";
import useTranslationModeContext from "./course/hooks/useTranslationModeContext";
import LanguageSelectorDropdown from "@src/features/contentDetail/ui/LanguageSelectorDropdown";
import LanguageIcon from "@src/lib/icons/18px/language";
import Sparkles4FillIcon from "@src/lib/icons/18px/sparkles-4-fill";
import { useTranslationCacheContext } from "@src/lib/contexts/TranslationCacheContext";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

type Props = {
  translationModeEnabled?: boolean;
  setTranslationModeEnabled: (enabled: boolean) => void;
  size?: "icon-md" | "icon-sm";
};

const TranslationDropdown: FC<Props> = ({
  translationModeEnabled = false,
  setTranslationModeEnabled,
  size = "icon-md",
}) => {
  const translationModeContext = useTranslationModeContext();
  const { hasPendingTranslations } = useTranslationCacheContext();
  const shouldReduceMotion = useReducedMotion();

  if (!translationModeContext) {
    return null;
  }

  const translationsLoading = hasPendingTranslations(
    translationModeContext.translationLanguage,
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size={size}
                  variant={translationModeEnabled ? "default" : "ghost"}
                >
                  <LanguageIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Translations</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex w-[250px] flex-col gap-2 overflow-visible rounded-2xl px-4 py-3"
        onInteractOutside={(e) => {
          const target = e.target as Element | null;
          if (target?.closest("[role='menu']")) {
            e.preventDefault();
          }
        }}
      >
        <div className="flex flex-1 items-center justify-between gap-2 self-stretch">
          <Text type="P2" fontWeight="Medium">
            Translation mode
          </Text>
          <Switch
            checked={translationModeEnabled}
            onCheckedChange={setTranslationModeEnabled}
          />
        </div>
        {translationModeEnabled && (
          <>
            <AnimatePresence initial={false}>
              {translationsLoading && (
                <motion.div
                  initial={
                    shouldReduceMotion
                      ? false
                      : { height: 0, opacity: 0, y: -4 }
                  }
                  animate={{ height: "auto", opacity: 1, y: 0 }}
                  exit={{ height: 0, opacity: 0, y: -4 }}
                  transition={{
                    duration: shouldReduceMotion ? 0 : 0.24,
                    ease: "easeOut",
                  }}
                  className="overflow-hidden"
                >
                  <div
                    role="status"
                    aria-live="polite"
                    className="relative overflow-hidden rounded-xl border border-purple-5 bg-gradient-to-br from-purple-1 via-white to-blue-1 p-3 shadow-bas-sm"
                  >
                    <div className="absolute -top-6 -right-4 size-16 rounded-full bg-purple-alpha-3 blur-xl" />
                    <div className="relative flex items-center gap-2.5">
                      <motion.div
                        animate={
                          shouldReduceMotion
                            ? undefined
                            : {
                                rotate: [0, 8, -6, 0],
                                scale: [1, 1.08, 1],
                              }
                        }
                        transition={
                          shouldReduceMotion
                            ? undefined
                            : { duration: 2, repeat: Infinity }
                        }
                        className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-ai text-white shadow-bas-sm"
                      >
                        <Sparkles4FillIcon
                          className="size-4"
                          aria-hidden="true"
                        />
                      </motion.div>
                      <div className="min-w-0">
                        <Text
                          type="P3"
                          fontWeight="SemiBold"
                          multiline
                          className="text-purple-12"
                        >
                          AI is generating your translations
                        </Text>
                        <Text type="P3" className="text-purple-9">
                          This may take a moment
                        </Text>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <LanguageSelectorDropdown
              selectedLanguage={translationModeContext.translationLanguage}
              onLanguageChange={translationModeContext.setTranslationLanguage}
              variant="light"
            />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default TranslationDropdown;
